import { mfaRequired } from '@promanage/core';
import { PUBLIC_PATHS } from './publicPaths';

export { PUBLIC_PATHS };

/**
 * What a session is allowed to see, decided in one place.
 *
 * Pure on purpose. The router guard and the layout both consult this: if the guard let a
 * route through that the layout renders bare, or the layout drew the rail around a login
 * page, the two would be disagreeing about whether the user is logged in — and the visible
 * outcome of that disagreement is an empty app shell that looks broken.
 *
 * NOTHING here is a security control. The server refuses every request on its own evidence;
 * this only decides what to draw and where to send someone next. Treating a client-side gate
 * as the enforcement point is how "hide the button" becomes the whole authorisation model.
 */

/** Needs a session, but not one that has satisfied MFA — this is where you go to satisfy it. */
export const MFA_PATH = '/mfa';

export function isPublicPath(pathname: string): boolean {
  return PUBLIC_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`));
}

/** Paths that render without the rail: the ones you see before you are properly in. */
export function isBarePath(pathname: string): boolean {
  return isPublicPath(pathname) || pathname === MFA_PATH;
}

/**
 * Control characters, which browsers strip while normalising a URL.
 *
 * Written as a constructed RegExp rather than a literal so the source file contains the
 * escapes and not the bytes — a raw NUL or newline inside a regex literal is invisible in
 * review and does not survive a copy-paste intact.
 */
// Matching control characters is the entire job here: they are what a smuggled CR/LF or NUL
// in `?redirect=` looks like before a browser tidies it away.
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = new RegExp('[\\u0000-\\u001f\\u007f]');

/**
 * Where to go after signing in, given the `?redirect=` the guard put in the URL.
 *
 * That parameter is attacker-controlled — anyone can send a link to
 * `/login?redirect=https://evil.example/`. Following it unchecked turns the login page into an
 * open redirect, which in this sector is the setup for the payment-redirection fraud in
 * docs/SECURITY.md §1: the victim really did log in to ProManage, and lands on a convincing
 * copy afterwards with the referrer to prove where they came from.
 *
 * So: an in-app absolute path and nothing else. Not a scheme, not a host, not a
 * protocol-relative `//host` (which browsers resolve as an absolute URL despite the leading
 * slash), not the back-slash variant of the same trick, and nothing carrying a control
 * character that would let the parse be smuggled past those checks.
 */
export function safeRedirect(target: string | undefined, fallback = '/'): string {
  if (!target) return fallback;
  if (!target.startsWith('/')) return fallback;
  if (target.startsWith('//') || target.startsWith('/\\')) return fallback;
  if (CONTROL_CHARS.test(target)) return fallback;
  // Bouncing back to a bare page would immediately redirect again; on /login that is a loop.
  if (isPublicPath(target.split('?')[0] ?? target)) return fallback;
  return target;
}

/** The shape of `auth.me`, reduced to what the gate actually reads. */
export interface GateSubject {
  roles: readonly string[];
  mfaEnrolled: boolean;
  /** ISO string, or null when this session has never satisfied MFA. */
  mfaVerifiedAt: string | null;
  /**
   * Whether the server asks for a second factor at all.
   *
   * Absent or true in every deployed environment — the API refuses to boot with its
   * development bypass set in production. Optional so this stays a structural type that a
   * session object from any older build still satisfies.
   */
  mfaEnforced?: boolean;
}

export type Gate =
  /** Nothing outstanding — show the application. */
  | 'ok'
  /** Enrolled, but this session has not produced a code yet. */
  | 'verify'
  /** The role demands a second factor and there is no authenticator set up. */
  | 'enrol';

/**
 * What this session still has to do.
 *
 * Mirrors apps/api authRouter.login exactly, and the order matters: enrolment is checked
 * FIRST, because an enrolled user must verify regardless of whether their roles require MFA.
 * The server sets `mfaVerifiedAt` to null for anyone enrolled — someone who volunteered a
 * second factor does not get it quietly skipped when their roles change.
 */
export function authGate(subject: GateSubject): Gate {
  // The server is the authority on whether it wants a second factor. Under the API's
  // development bypass it does not, and sending the user to enrol anyway would be this file
  // demanding something the server will not check — the client-side gate deciding policy,
  // which is the one thing the note at the top of this file says it must never do.
  if (subject.mfaEnforced === false) return 'ok';

  if (subject.mfaEnrolled) return subject.mfaVerifiedAt === null ? 'verify' : 'ok';
  return mfaRequired(subject.roles) ? 'enrol' : 'ok';
}

/**
 * The path the shell is CURRENTLY RENDERING — which during a navigation is not
 * `state.location.pathname`.
 *
 * TanStack Router sets `state.location` to the DESTINATION the moment a navigation starts,
 * then leaves the previous matches mounted while `beforeLoad` and the loaders run. So between
 * "Sign in" succeeding and the dashboard's data arriving, `state.location.pathname` is `/`
 * while `<Outlet/>` is still rendering `/login`. A layout that picks its chrome from the
 * location therefore wraps the login card in the full application shell — rail, mobile nav and
 * all — for the length of two round trips. That is the flash.
 *
 * `state.matches` is the store the Outlet itself renders from, committed in the same
 * transaction, so a decision taken from it can never disagree with what is on screen.
 */
export function renderedPathname(
  matches: readonly { pathname: string }[],
  pending: string,
): string {
  // Empty only before the first match commits, when the router is rendering nothing anyway.
  return matches.at(-1)?.pathname ?? pending;
}
